/**
 * Safely removes a DOM node from its parent without throwing errors.
 * Handles edge cases like:
 * - Node already removed
 * - Node no longer a child of parent
 * - Node moved to different parent
 * - Race conditions in cleanup functions
 *
 * @param {HTMLElement|null|undefined} node - The DOM node to remove
 * @returns {boolean} - True if node was removed, false otherwise
 */
export function safeRemoveNode(node) {
  if (!node) {
    return false;
  }

  try {
    // Modern approach: use .remove() if available (supported in all modern browsers)
    if (typeof node.remove === 'function') {
      node.remove();
      return true;
    }

    // Fallback for older browsers: use removeChild with safety checks
    if (node.parentNode) {
      node.parentNode.removeChild(node);
      return true;
    }

    return false;
  } catch (error) {
    // Silently catch NotFoundError and other DOM exceptions
    // This prevents the application from crashing when cleanup happens multiple times
    console.debug('safeRemoveNode: Node removal failed (likely already removed)', error.message);
    return false;
  }
}

/**
 * Safely removes a DOM node by ID.
 *
 * @param {string} elementId - The ID of the element to remove
 * @returns {boolean} - True if node was found and removed, false otherwise
 */
export function safeRemoveById(elementId) {
  const element = document.getElementById(elementId);
  return safeRemoveNode(element);
}

/**
 * Safely removes all child nodes from a parent element.
 *
 * @param {HTMLElement|null|undefined} parentNode - The parent element
 * @returns {number} - Number of children removed
 */
export function safeRemoveAllChildren(parentNode) {
  if (!parentNode) {
    return 0;
  }

  let removedCount = 0;
  try {
    while (parentNode.firstChild) {
      const child = parentNode.firstChild;
      safeRemoveNode(child);
      // If the child was not actually removed (safeRemoveNode swallowed an error),
      // break to avoid an infinite loop.
      if (parentNode.firstChild === child) break;
      removedCount++;
    }
  } catch (error) {
    console.debug('safeRemoveAllChildren: Error during cleanup', error.message);
  }

  return removedCount;
}
